import type { Settings } from '@tonist/shared';
import { FIFTHS_ORDER, PITCH_CLASS_NAMES, SCALES } from '@tonist/shared';
import { useSettings } from '../../store/settingsStore';


const PITCH_ENGINES: Array<[Settings['pitchEngine'], string, string]> = [
  ['multisample', 'Multisample', 'Roots rendered every 6 semitones with a phase vocoder, the last ±3 semitones by resampling. Natural over a wide range; the default.'],
  ['multisample-formant', 'Multisample + formant', 'Same, with formant correction: keeps an instrument\'s body (its resonances) where it is when the pitch moves far.'],
  ['resample', 'Resample', 'The loop simply plays faster or slower, like a classic sampler. Cleanest sound; timbre and loop speed move with the pitch.'],
  ['granular', 'Granular', 'Time-domain granular shifting at real speed: crisp and lively, slightly grainy on sustained tones, no vocoder smear.'],
  ['live-stretch', 'Live stretch', 'A real-time phase vocoder per voice (formant-locked when the drone asks for it). Heaviest on the CPU, ~100 ms pitch latency.'],
];

export function TuneSheet({ onClose }: { onClose: () => void }) {
  const s = useSettings();
  const scales = s.showExtraScales ? SCALES : SCALES.filter((sc) => !sc.extra);
  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="glass sheet" role="dialog" aria-label="Tune">
        <div className="row">
          <h2>Tune</h2>
          <span className="grow" />
          <button type="button" className="btn btn--sm btn--ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <section className="sheet__section" aria-label="Tonic">
          <div className="eyebrow"><span>Tonic (Sa) — circle of fifths</span><span>{PITCH_CLASS_NAMES[s.tonicPc]}</span></div>
          <div className="chips" role="radiogroup" aria-label="Tonic">
            {FIFTHS_ORDER.map((pc) => (
              <button key={pc} type="button" role="radio" className="chip" aria-checked={s.tonicPc === pc} aria-pressed={s.tonicPc === pc} onClick={() => s.setTonic(pc)}>
                {PITCH_CLASS_NAMES[pc]}
              </button>
            ))}
          </div>
        </section>

        <section className="sheet__section" aria-label="Scale">
          <div className="eyebrow">
            <span>Scale</span>
            <button type="button" className="btn btn--sm btn--ghost" aria-pressed={s.showExtraScales} onClick={s.toggleExtraScales}>{s.showExtraScales ? 'fewer' : 'more'}</button>
          </div>
          <div className="scale-list" role="radiogroup" aria-label="Scale">
            {scales.map((sc, i) => (
              <button key={sc.id} type="button" role="radio" className="scale-item" aria-checked={s.scaleId === sc.id} aria-pressed={s.scaleId === sc.id} onClick={() => s.setScale(sc.id)}>
                <b>{i < 10 ? <kbd style={{ marginRight: 6 }}>{(i + 1) % 10}</kbd> : null}{sc.raga} <span>({sc.mode})</span></b>
                <span>{sc.character}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="sheet__section" aria-label="Options">
          <div className="row">
            <div className="seg" role="group" aria-label="Intonation">
              <button type="button" aria-pressed={s.ji} onClick={() => s.set({ ji: true })}>Just</button>
              <button type="button" aria-pressed={!s.ji} onClick={() => s.set({ ji: false })}>12-TET</button>
            </div>
            <button type="button" className="btn" aria-pressed={s.calm} onClick={() => s.set({ calm: !s.calm })}>Calm {s.calm ? 'on' : 'off'}</button>
            <button type="button" className="btn" aria-pressed={s.debugAudio} title="Verbose audio logging in the browser console (F12)" onClick={() => s.set({ debugAudio: !s.debugAudio })}>Debug audio {s.debugAudio ? 'on' : 'off'}</button>
            <div className="seg" role="group" aria-label="Theme">
              {(['dark', 'light', 'system'] as const).map((t) => (
                <button key={t} type="button" aria-pressed={s.theme === t} onClick={() => s.set({ theme: t })}>{t}</button>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="label">Pitch engine — how sampled drones are re-pitched (compare them; multisample is the default)</span>
            <div className="seg seg--wrap" role="group" aria-label="Pitch engine">
              {PITCH_ENGINES.map(([k, label, hint]) => (
                <button key={k} type="button" aria-pressed={s.pitchEngine === k} title={hint} onClick={() => s.set({ pitchEngine: k })}>{label}</button>
              ))}
            </div>
            <small className="hint">{PITCH_ENGINES.find(([k]) => k === s.pitchEngine)?.[2]}</small>
          </div>
          <label className="field" style={{ maxWidth: 320 }}>
            <span className="label">Volume — {Math.round(s.volume * 100)}%</span>
            <input className="range" type="range" min={0} max={1} step={0.01} value={s.volume} onChange={(e) => s.set({ volume: Number(e.target.value) })} />
          </label>
        </section>
      </div>
    </>
  );
}
